import React, { useCallback, useMemo, useState } from 'react'
import SimCardsTable from './components/sim-cards-table'
import SimCardsTableSearch from './components/sim-cards-table-search'
import { makeStyles } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { useSimCardsFilters } from '../root/hooks/use-sim-cards-filters'
import { useLocation } from 'react-router-dom'
import { urlToSearchState } from 'web/src/utils/hooks/useSyncUrlState'
import { useQuery } from 'react-query'
import { getSimCards, updateSimCardStatus } from './api/sim-cards-api'
import { debounce } from 'lodash'
import { isEmptyList } from 'web/src/utils'
import { useMutation } from 'react-query'
import { enqueueSnackbar } from '../root/actions'
import SimCardBillingDetails from './components/sim-card-billing-details'
import { useSimCardsApi } from '../root/hooks/use-sim-cards-api'

export default function SimCards() {
    const dispatch = useDispatch()
    const loc = useLocation()

    const classes = useStyles()
    const { locale } = useSelector(state => state.main)

    const [filters, d] = useSimCardsFilters(urlToSearchState(loc))
    const [isFetchedAllSimCards, setisFetchedAllSimCards] = useState(false)
    const [simCards, setSimCards] = useState([])

    const filtersQuery = useMemo(() => {
        return `?${Object.keys(filters)
            .map(key => `${key}=${filters[key]}`)
            .join('&')}`
    }, [filters])

    const { isLoading } = useQuery(
        ['fetach-sim-cards', filtersQuery],
        () => getSimCards(filtersQuery),
        {
            refetchOnWindowFocus: false,
            onSuccess: data => {
                if (isEmptyList(data)) {
                    setisFetchedAllSimCards(true)
                    return
                }

                setisFetchedAllSimCards(false)
                setSimCards([
                    ...simCards,
                    ...data.map(simCard => {
                        const {
                            sim_serial_number,
                            network_device_serial_number,
                            ...rest
                        } = simCard
                        return {
                            simSerialNumber: sim_serial_number,
                            networkDeviceSerialNumber:
                                network_device_serial_number,
                            ...rest
                        }
                    })
                ])
            },
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.getSimCardEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const {
        createSimCard,
        isCreatingSimCard,
        isSimCardCreationSuccessful,
        isSimCardCreationError
    } = useSimCardsApi({
        locale: locale,
        refetch: () => {
            setSimCards([])
            d({
                type: 'skipAndTake',
                skipAndTake: {
                    skip: 0,
                    take: 20
                }
            })
        }
    })

    const { mutate: updateSimCardMutation } = useMutation(
        payload => updateSimCardStatus(payload),
        {
            onSuccess: data => {
                updateSimCardState(data)
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.updateSimCardRes,
                        options: {
                            variant: 'success'
                        }
                    })
                )
            },
            onError: () => {
                dispatch(
                    enqueueSnackbar({
                        message: locale.sagasMsgs.updateSimCardEx,
                        options: {
                            variant: 'error'
                        }
                    })
                )
            }
        }
    )

    const updateSimCardState = updatedSimCard => {
        const { sim_serial_number, network_device_serial_number, ...rest } =
            updatedSimCard

        setSimCards(
            simCards.map(simCard => {
                if (simCard.simSerialNumber === sim_serial_number) {
                    return {
                        simSerialNumber: sim_serial_number,
                        networkDeviceSerialNumber: network_device_serial_number,
                        ...rest
                    }
                }

                return simCard
            })
        )
    }

    const updateSimCard = ({ simSerialNumber, status }) =>
        updateSimCardMutation({ simSerialNumber, status })

    const onSearch = searchValue => {
        setSimCards([])
        d({ type: 'search', value: searchValue.trim() })
    }

    const onSort = sort_by => {
        setSimCards([])
        d({ type: 'sort', sort_by })
    }

    const onLoaderMore = useCallback(
        debounce(() => {
            if (isFetchedAllSimCards) return
            const currentTake = filters.take
            const currentSkip = filters.skip

            d({
                type: 'skipAndTake',
                skipAndTake: {
                    skip: currentSkip + currentTake,
                    take: currentTake
                }
            })
        }, 300)
    )

    return (
        <div className={classes.wrapper}>
            <div className={classes.toolbarWrapper}>
                {/* <SimCardBillingDetails locale={locale} /> */}
                <div className={classes.searchWrapper}>
                    <SimCardsTableSearch
                        locale={locale}
                        onSearch={onSearch}
                        filters={filters}
                    />
                </div>
            </div>
            <SimCardsTable
                simCards={simCards}
                search={filters.search}
                sorting={filters.sort_by}
                onSort={onSort}
                isLoading={isLoading}
                onLoaderMore={onLoaderMore}
                updateSimCard={updateSimCard}
                createSimCard={createSimCard}
                isCreatingSimCard={isCreatingSimCard}
                isSimCardCreationSuccessful={isSimCardCreationSuccessful}
                isSimCardCreationError={isSimCardCreationError}
            />
        </div>
    )
}

const useStyles = makeStyles(theme => ({
    wrapper: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    toolbarWrapper: {
        width: '100%',
        display: 'flex',
        marginBottom: '20px',
        justifyContent: 'space-between'
    },
    searchWrapper: {
        display: 'flex',
        maxWidth: 260,
        alignItems: 'flex-end'
    }
}))
